import { cloneElement, memo, useMemo } from 'react';

import { skipToken } from '@reduxjs/toolkit/query';
import PropTypes from 'prop-types';

import FinalcadImage from 'assets/images/logos/finalcad-logo-dark.svg';
import config from 'config/config';
import { useGetImageUrlQuery } from 'redux/api/mediaService.api';
import { quality } from 'utils/constants';
import { isValidSecureURL } from 'utils/validators';

const SecuredImage = ({
  children,
  componentSrc = 'src',
  imgQuality = quality.original,
  placeholder = FinalcadImage,
  withPlaceholder = true,
  forceRefresh = false,
}) => {
  const rawSrc = children?.props?.[componentSrc];

  const imageURL = useMemo(
    () => {
      if (!rawSrc) return null;
      if (config.overridesMediaServiceUrl) {
        return rawSrc.replace(
          /https:\/\/api(\.eu|\.ap)?\.(sandbox|staging)\.finalcad\.cloud/gi,
          config.apiHostGateway,
        );
      }
      return rawSrc;
    },
    [rawSrc],
  );

  const shouldFetchSecureImage = isValidSecureURL(imageURL);

  const { data: fetchedImageUrl } = useGetImageUrlQuery(
    shouldFetchSecureImage ? { url: imageURL, quality: imgQuality } : skipToken,
    { refetchOnMountOrArgChange: forceRefresh },
  );

  const finalSrc = useMemo(
    () => {
      if (shouldFetchSecureImage) {
        return fetchedImageUrl || (withPlaceholder ? placeholder : null);
      }
      return imageURL || (withPlaceholder ? placeholder : null);
    },
    [shouldFetchSecureImage, fetchedImageUrl, imageURL, withPlaceholder, placeholder],
  );

  return cloneElement(children, {
    [componentSrc]: finalSrc,
  });
};

SecuredImage.propTypes = {
  children: PropTypes.element.isRequired,
  componentSrc: PropTypes.string,
  imgQuality: PropTypes.oneOf(Object.values(quality)),
  placeholder: PropTypes.string,
  withPlaceholder: PropTypes.bool,
  forceRefresh: PropTypes.bool,
};

export default memo(SecuredImage);
